// pages/LogCenterPage.js - 日志中心
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Select, Row, Col, Button, DatePicker, Input, Modal, Popconfirm, message } from 'antd';
import { FileTextOutlined, LoginOutlined, LogoutOutlined, ToolOutlined, DeleteOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', text: '#fff', muted: 'rgba(255,255,255,0.6)' };
const { RangePicker } = DatePicker;

const actionOptions = (t) => [
  { value: 'login', label: t('log.action_login') }, { value: 'logout', label: t('log.action_logout') },
  { value: 'add_device', label: t('log.action_add_device') }, { value: 'update_device', label: t('log.action_update_device') },
  { value: 'delete_device', label: t('log.action_delete_device') },
  { value: 'add_user', label: t('log.action_add_user') }, { value: 'update_user', label: t('log.action_update_user') },
  { value: 'delete_user', label: t('log.action_delete_user') },
  { value: 'add_workorder', label: t('log.action_add_workorder') }, { value: 'update_workorder', label: t('log.action_update_workorder') },
  { value: 'delete_workorder', label: t('log.action_delete_workorder') },
  { value: 'add_alert', label: t('log.action_add_alert') }, { value: 'handle_alert', label: t('log.action_handle_alert') },
  { value: 'delete_alert', label: t('log.action_delete_alert') },
  { value: 'update_config', label: t('log.action_update_config') }, { value: 'export_data', label: t('log.action_export_data') },
];

const actionIcon = {
  login: <LoginOutlined />, logout: <LogoutOutlined />,
  add_device: <ToolOutlined />, update_device: <ToolOutlined />, delete_device: <ToolOutlined />,
  add_user: <ToolOutlined />, update_user: <ToolOutlined />, delete_user: <ToolOutlined />,
  add_workorder: <ToolOutlined />, update_workorder: <ToolOutlined />, delete_workorder: <ToolOutlined />,
  add_alert: <ToolOutlined />, handle_alert: <ToolOutlined />, delete_alert: <ToolOutlined />,
  update_config: <ToolOutlined />, export_data: <ToolOutlined />,
};
const actionColor = {
  login: 'blue', logout: 'default',
  add_device: 'green', update_device: 'orange', delete_device: 'red',
  add_user: 'green', update_user: 'orange', delete_user: 'red',
  add_workorder: 'green', update_workorder: 'orange', delete_workorder: 'red',
  add_alert: 'green', handle_alert: 'cyan', delete_alert: 'red',
  update_config: 'purple', export_data: 'geekblue',
};
const actionText = (t) => ({
  login: t('log.action_login'), logout: t('log.action_logout'),
  add_device: t('log.action_add_device'), update_device: t('log.action_update_device'), delete_device: t('log.action_delete_device'),
  add_user: t('log.action_add_user'), update_user: t('log.action_update_user'), delete_user: t('log.action_delete_user'),
  add_workorder: t('log.action_add_workorder'), update_workorder: t('log.action_update_workorder'), delete_workorder: t('log.action_delete_workorder'),
  add_alert: t('log.action_add_alert'), handle_alert: t('log.action_handle_alert'), delete_alert: t('log.action_delete_alert'),
  update_config: t('log.action_update_config'), export_data: t('log.action_export_data'),
});

const LogCenterPage = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, week: 0, login: 0, logout: 0, topActions: [] });
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ action: '', username: '', start_date: '', end_date: '', page: 1, pageSize: 20 });
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLog, setDetailLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filter.page, pageSize: filter.pageSize };
      if (filter.action) params.action = filter.action;
      if (filter.username) params.username = filter.username;
      if (filter.start_date) params.start_date = filter.start_date;
      if (filter.end_date) params.end_date = filter.end_date;
      const json = await api.getLogList(params);
      if (json.success) { setLogs(json.data || []); setTotal(json.total || 0); }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const json = await api.getLogStats();
      if (json.success) setStats(json.stats || { total: 0, today: 0, week: 0, login: 0, logout: 0, topActions: [] });
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDelete = async (id) => {
    try {
      const res = await api.deleteLog(id);
      if (res.success) { message.success(t('log.deleteSuccess')); fetchLogs(); fetchStats(); }
      else message.error(t('log.deleteFailed'));
    } catch { message.error(t('log.deleteFailed')); }
  };

  const handleCleanAll = async () => {
    try {
      const res = await api.cleanLog();
      if (res.success) { message.success(t('log.logsCleared')); fetchLogs(); fetchStats(); }
      else message.error(t('log.clearFailed'));
    } catch { message.error(t('log.clearFailed')); }
  };

  const handleDetail = (record) => { setDetailLog(record); setDetailVisible(true); };

  const columns = [
    { title: t('log.time'), dataIndex: 'created_at', key: 'created_at', width: 160,
      render: val => <span style={{ color: COLORS.muted }}>{moment(val).format('YYYY-MM-DD HH:mm:ss')}</span> },
    { title: t('log.user'), dataIndex: 'username', key: 'username', width: 120,
      render: val => <span style={{ color: COLORS.primary, fontWeight: 600 }}>{val}</span> },
    { title: t('log.operationType'), dataIndex: 'action', key: 'action', width: 130,
      render: val => <Tag icon={actionIcon[val]} color={actionColor[val]}>{actionText(t)[val] || val}</Tag> },
    { title: t('log.target'), dataIndex: 'target', key: 'target',
      render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120,
      render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: t('log.action'), key: 'action_col', width: 100, fixed: 'right',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" icon={<InfoCircleOutlined />} style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }} onClick={() => handleDetail(r)} />
          <Popconfirm title={t('log.confirmDelete')} onConfirm={() => handleDelete(r.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </div>
      )
    },
  ];

  const statCards = [
    { label: t('log.totalLogs'), value: stats.total, color: COLORS.primary, icon: <FileTextOutlined /> },
    { label: t('log.todayLogs'), value: stats.today, color: '#f4c542', icon: <FileTextOutlined /> },
    { label: t('log.last7Days'), value: stats.week, color: '#52e8b0', icon: <FileTextOutlined /> },
    { label: t('log.loginCount'), value: stats.login, color: '#5b8def', icon: <LoginOutlined /> },
    { label: t('log.logoutCount'), value: stats.logout, color: 'rgba(255,255,255,0.4)', icon: <LogoutOutlined /> },
  ];

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {statCards.map(item => (
          <Col xs={12} sm={8} md={5} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '16px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                  {React.cloneElement(item.icon, { style: { fontSize: 18, color: item.color } })}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: COLORS.muted }}>{item.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff' }}>{t('log.title')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchLogs(); fetchStats(); }}
              style={{ background: 'rgba(0,180,216,0.15)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.3)' }}>{t('common.refresh')}</Button>
            <Popconfirm title={t('log.confirmCleanAll')} onConfirm={handleCleanAll}>
              <Button icon={<DeleteOutlined />} danger>{t('log.clearLogs')}</Button>
            </Popconfirm>
          </div>
        </div>

        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Input placeholder={t('log.searchUser')} allowClear style={{ width: 140 }}
            onChange={e => setFilter(f => ({ ...f, username: e.target.value || '', page: 1 }))} />
          <Select placeholder={t('log.operationType')} allowClear style={{ width: 140 }}
            onChange={v => setFilter(f => ({ ...f, action: v || '', page: 1 }))}
            options={actionOptions(t)} />
          <RangePicker style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(0,180,216,0.3)' }}
            onChange={(dates, dateStrings) => {
              setFilter(f => ({ ...f, start_date: dateStrings[0] || '', end_date: dateStrings[1] || '', page: 1 }));
            }} />
        </div>

        <Table columns={columns} dataSource={logs} loading={loading} rowKey="id" scroll={{ x: 900 }}
          pagination={{ current: filter.page, pageSize: filter.pageSize, total, onChange: (page, ps) => setFilter(f => ({ ...f, page, pageSize: ps })) }}
          style={{ background: 'transparent' }}
        />
      </Card>

      <Modal title={<span style={{ color: COLORS.text }}>{t('log.detailTitle')}</span>} open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null}
        styles={{ mask: { background: 'rgba(0,0,0,0.6)' }, content: { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.3)' } }}>
        {detailLog && (
          <div style={{ color: COLORS.muted }}>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>{t('log.time')}：</span>{moment(detailLog.created_at).format('YYYY-MM-DD HH:mm:ss')}</div>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>{t('log.user')}：</span>{detailLog.username}</div>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>{t('log.action')}：</span><Tag color={actionColor[detailLog.action]}>{actionText(t)[detailLog.action] || detailLog.action}</Tag></div>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>{t('log.target')}：</span>{detailLog.target || '-'}</div>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>IP：</span>{detailLog.ip || '-'}</div>
            <div style={{ marginBottom: 12 }}><span style={{ color: COLORS.primary }}>详情：</span>{detailLog.detail || '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LogCenterPage;
